import React from 'react';
import qs from 'qs';
import { bool, string, shape } from 'prop-types';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router';
import {
  Tabs,
  TabsItem,
  Gutter,
  Layout,
  LayoutItem,
  Suspense,
  Card,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';

import { OpportunityProfileHeader } from '../OpportunityProfileHeader/OpportunityProfileHeader';
import OpportunityProfileDetailForm from '../OpportunityProfileDetailForm/OpportunityProfileDetailForm';
import OpportunityEngagements from '../OpportunityEngagements/OpportunityEngagements';
import ActivityLog from '../ActivityLog/ActivityLog';
import StoreStateTreatment from '../StoreStateTreatment/StoreStateTreatment';
import { OpportunitySubmission } from '../OpportunitySubmission/OpportunitySubmission';
import {
  opportunityFormInitialValues,
  opportunityShape,
} from '../../models/opportunities-const';
import { makeOpportunitiesViewSelector } from '../../redux/selectors/opportunities-profile-component-selector';
import { window } from '../../utils/global-util';
import { PreloadedUserStore } from '../../stores/preloaded-user-store';
import { EngagementsUtils } from '../../utils/engagements-util';
import { OpportunityEngagementsStore } from '../../stores/opportunity-engagements-store';
import { OpportunityPositionsStore } from '../../stores/opportunity-positions-store';
import { OpportunityStore } from '../../stores/opportunity-store';
import { formatDataTestId } from '../../utils/data-test-util';
import { EventsStore } from '../../stores/events-store';
import { WindowUtil } from '../../utils/window-util';
import { WithRouterShape } from '../../models/router';
import { useRouterBundle } from '../../hooks/useRouterBundle';
import { EditOpportunityTalentTabStore } from '../../stores/edit-opportunity-talent-tab-store';
import { OpportunityTalent } from '../OpportunityTalent/OpportunityTalent';
import { OpportunityInterviews } from '../OpportunityInterviews/OpportunityInterviews';
import { Onboarding } from '../Onboarding/Onboarding';

import {
  JobProfileNoItems,
  OppDetailTabsBlock,
} from './OpportunityDetailsStyles';

const onBoardingValidStageCodes = new Set(['3', 'W']);

class OpportunityDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: '',
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let { searchTerm } = nextProps;
    if (nextProps.isDetail) {
      const params = window && window.location && window.location.search;
      if (params) {
        const parsed = qs.parse(params.slice(1));
        searchTerm = parsed.search;
      }
    }
    return searchTerm === prevState.searchTerm
      ? null
      : { ...prevState, searchTerm };
  }

  componentWillUnmount() {
    const { expanded } = this.props;
    if (expanded) {
      WindowUtil.title('Axiom for HQ');
    }
  }

  renderTabs(
    engagements,
    eventsData,
    positions,
    editOpportunityTalentTabState
  ) {
    const {
      props: {
        isDetail,
        user,
        opportunity,
        router: { location, params, navigate },
      },
    } = this;

    const engagementsCount = EngagementsUtils.countEngagements(
      opportunity,
      engagements
    );

    let rootPath = 'opportunities';

    if (location.pathname?.includes('opportunity-detail')) {
      rootPath = 'opportunity-detail';
    } else if (location.pathname?.includes('workspace')) {
      rootPath = 'workspace/opportunities';
    }

    const basePath = `/${rootPath}/${params.id}`;

    const opportunityValues = opportunityFormInitialValues(opportunity);

    const goToTab = tabSlug => {
      navigate(WindowUtil.appendQueryParams(`${basePath}/${tabSlug}`));
    };

    const getStartTab = () => {
      // TODO: remove opportunities once sidedrawer is removed (HAL-1970)
      // this should a redirect in the Router.jsx
      const activityLogRegex =
        /(opportunities|opportunity-detail)\/[\da-f]{8}-[\da-f]{4}-[0-5][\da-f]{3}-[089ab][\da-f]{3}-[\da-f]{12}\/activitylog/i;

      switch (true) {
        case activityLogRegex.test(location.pathname): {
          return 'activity-log';
        }
        case location.pathname?.includes('/engagements'): {
          return 'engagements';
        }
        case location.pathname?.includes('/submission'): {
          return 'submission';
        }
        case location.pathname?.includes('/talent'): {
          return 'talent';
        }
        case location.pathname?.includes('/interviews'): {
          return 'interviews';
        }
        case location.pathname?.includes('/onboarding'): {
          return 'onboarding';
        }
        case location.pathname?.includes('/feedback'): {
          return 'feedback';
        }
        default: {
          return 'profile';
        }
      }
    };

    return (
      <Tabs startTab={getStartTab()} fluid scrollable stretched>
        <TabsItem
          name="profile"
          label="Details"
          onClick={() => {
            goToTab('profile');
          }}
        >
          <Gutter bottom="16px" />
          <Card>
            <Layout background="panelLight">
              <LayoutItem fluid>
                <Gutter horizontal="16px">
                  <OpportunityProfileDetailForm
                    form="editOpportunity"
                    initialValues={opportunityValues}
                    isDetail={isDetail}
                    isEnvoySalesUser={user.roles.includes('EnvoySales')}
                    opportunity={opportunity}
                  />
                </Gutter>
              </LayoutItem>
            </Layout>
          </Card>
        </TabsItem>
        <TabsItem
          name="talent"
          label="Talent"
          countBadge={
            (editOpportunityTalentTabState?.data?.candidates ?? []).some(
              ({ candidateStatus }) =>
                !opportunity.isClosed &&
                [
                  CandidateOpportunitiesConst.CandidateStatuses.Interested,
                  CandidateOpportunitiesConst.MLRecommendationStatuses
                    .AiSuggested,
                ].includes(candidateStatus)
            ) || undefined
          }
          onClick={() => {
            goToTab('talent');
          }}
        >
          <Gutter top="16px">
            <OppDetailTabsBlock>
              <Card background={null}>
                <Layout>
                  <LayoutItem fluid>
                    <OpportunityTalent />
                  </LayoutItem>
                </Layout>
              </Card>
            </OppDetailTabsBlock>
          </Gutter>
        </TabsItem>
        <TabsItem
          name="submission"
          label="Submission"
          onClick={() => {
            goToTab('submission');
          }}
        >
          <OppDetailTabsBlock>
            <Suspense stretched>
              <Gutter vertical="16px">
                <OpportunitySubmission opportunity={opportunity} />
              </Gutter>
            </Suspense>
          </OppDetailTabsBlock>
        </TabsItem>
        <TabsItem
          label="Interviews"
          name="interviews"
          onClick={() => {
            goToTab('interviews');
          }}
        >
          <Suspense stretched>
            <OpportunityInterviews opportunity={opportunity} />
          </Suspense>
        </TabsItem>
        <TabsItem
          name="activity-log"
          label="Activity Log"
          onClick={() => {
            goToTab('activitylog');
          }}
        >
          <Gutter bottom="16px" />
          <Card>
            <Layout stretched>
              <LayoutItem fluid stretched>
                <Gutter horizontal="16px">
                  <ActivityLog eventsData={eventsData} positions={positions} />
                </Gutter>
              </LayoutItem>
            </Layout>
          </Card>
        </TabsItem>
        <TabsItem
          name="engagements"
          label="Engagements"
          countBadge={engagementsCount || undefined}
          onClick={() => {
            goToTab('engagements');
          }}
        >
          <Gutter bottom="16px" />
          <Card>
            <Layout background="panelLight">
              <LayoutItem fluid>
                <Gutter horizontal="16px">
                  <Suspense>
                    <OpportunityEngagements
                      engagementsCount={engagementsCount}
                      opportunity={opportunity}
                    />
                  </Suspense>
                </Gutter>
              </LayoutItem>
            </Layout>
          </Card>
        </TabsItem>
        {onBoardingValidStageCodes.has(opportunity.stageCode.toUpperCase()) ? (
          <TabsItem
            name="onboarding"
            label="Onboarding"
            onClick={() => {
              goToTab('onboarding');
            }}
          >
            <Gutter bottom="16px" />
            <Onboarding name="ONBOARDING_CONTAINER" opportunity={opportunity} />
          </TabsItem>
        ) : null}
      </Tabs>
    );
  }

  render() {
    const {
      state: { searchTerm },
      props: {
        expanded,
        isDetail,
        opportunityState,
        opportunityEngagementsState,
        opportunityPositionsState,
        eventsState,
        editOpportunityTalentTabState,
      },
    } = this;

    return (
      <StoreStateTreatment
        name="ACTIVE_OPPORTUNITY"
        stretched
        storeState={[
          opportunityState,
          opportunityEngagementsState,
          opportunityPositionsState,
          eventsState,
        ]}
        renderNonLoadedView={() => (
          <JobProfileNoItems
            data-test={formatDataTestId('no selected opportunity')}
          >
            Please select an opportunity from the list.
          </JobProfileNoItems>
        )}
        renderLoadedView={({
          data: [opportunity, engagements, positions, eventsData],
        }) => {
          if (expanded) {
            WindowUtil.title(opportunity.jobName);
          }
          return (
            <>
              <Gutter bottom="12px" />
              <OpportunityProfileHeader opportunity={opportunity} />
              <Gutter bottom="16px" />
              <Routes>
                {[`/:view/:viewId/*`, `/:view`, '/'].map(p => (
                  <Route
                    key={p}
                    path={p}
                    element={this.renderTabs(
                      engagements,
                      eventsData,
                      positions,
                      editOpportunityTalentTabState
                    )}
                  />
                ))}
              </Routes>
            </>
          );
        }}
      />
    );
  }
}

OpportunityDetails.defaultProps = {
  expanded: false,
  isDetail: false,
  // connect
  opportunity: null,
  searchTerm: null,
  user: null,
};

OpportunityDetails.propTypes = {
  editOpportunityTalentTabState:
    EditOpportunityTalentTabStore.getStateShape().isRequired,
  eventsState: EventsStore.getStateShape().isRequired,
  expanded: bool,
  isDetail: bool,
  // connect
  opportunity: opportunityShape,
  opportunityEngagementsState:
    OpportunityEngagementsStore.getStateShape().isRequired,
  opportunityPositionsState:
    OpportunityPositionsStore.getStateShape().isRequired,
  opportunityState: OpportunityStore.getStateShape().isRequired,
  router: shape(WithRouterShape).isRequired,
  searchTerm: string,
  user: PreloadedUserStore.getDataShape(),
};

// eslint-disable-next-line react-hooks/rules-of-hooks
export default useRouterBundle(
  connect(makeOpportunitiesViewSelector)(OpportunityDetails)
);
